(() => {
  const STORAGE_KEY = 'giraffeRecentlyViewed';
  const MAX_STORED_PRODUCTS = 20;

  /**
   * localStorageから閲覧履歴を取得
   */
  const getRecentlyViewedProducts = () => {
    try {
      const storedProducts = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      );

      if (!Array.isArray(storedProducts)) {
        return [];
      }

      return storedProducts.filter(
        (handle) => typeof handle === 'string' && handle !== ''
      );
    } catch (error) {
      console.warn('Recently viewed products could not be loaded.', error);
      return [];
    }
  };

  /**
   * 閲覧履歴をlocalStorageへ保存
   */
  const saveRecentlyViewedProducts = (products) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(products.slice(0, MAX_STORED_PRODUCTS))
      );
    } catch (error) {
      console.warn('Recently viewed products could not be saved.', error);
    }
  };

  /**
   * 現在の商品を閲覧履歴の先頭へ追加
   */
  const addCurrentProduct = (currentHandle) => {
    if (!currentHandle) {
      return getRecentlyViewedProducts();
    }

    const products = getRecentlyViewedProducts();

    const updatedProducts = [
      currentHandle,
      ...products.filter((handle) => handle !== currentHandle)
    ].slice(0, MAX_STORED_PRODUCTS);

    saveRecentlyViewedProducts(updatedProducts);

    return updatedProducts;
  };

  /**
   * Section Rendering APIから商品カードを取得
   *
   * 現在使用している「最近チェックした商品」セクション自身を、
   * 対象商品のURL上で再レンダリングする。
   *
   * これによりLiquidのcard-productをそのまま利用できる。
   */
  const fetchProductCard = async (handle, sectionId) => {
    const root = window.Shopify?.routes?.root || '/';

    const url = `${root}products/${encodeURIComponent(
      handle
    )}?section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        return null;
      }

      const html = await response.text();

      if (!html.trim()) {
        return null;
      }

      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(html, 'text/html');

      const template = documentFragment.querySelector(
        '[data-recently-viewed-card-template]'
      );

      if (!template) {
        return null;
      }

      const card = template.content.firstElementChild;

      if (!card) {
        return null;
      }

      return card.cloneNode(true);
    } catch (error) {
      console.warn(
        `Recently viewed product "${handle}" could not be loaded.`,
        error
      );

      return null;
    }
  };

  /**
   * Dawnのslider-componentを再初期化
   *
   * 商品カードはJSであとから追加されるため、
   * slider-componentが最初に取得したsliderItemsを更新する。
   */
  const refreshSlider = (section) => {
    const sliderComponent = section.querySelector(
      '[data-recently-viewed-slider]'
    );

    if (!sliderComponent) {
      return;
    }

    const slider = section.querySelector('[data-recently-viewed-list]');

    if (!slider) {
      return;
    }

    if ('sliderItems' in sliderComponent) {
      sliderComponent.sliderItems = slider.querySelectorAll('.slider__slide');
    }

    if (typeof sliderComponent.initPages === 'function') {
      sliderComponent.initPages();
    }

    if (typeof sliderComponent.update === 'function') {
      sliderComponent.update();
    }
  };

  /**
   * スライダーボタンの表示判定
   */
  const updateSliderButtons = (section, productCount) => {
    const sliderButtons = section.querySelector(
      '[data-recently-viewed-slider-buttons]'
    );

    const total = section.querySelector('[data-recently-viewed-total]');

    if (total) {
      total.textContent = productCount;
    }

    if (!sliderButtons) {
      return;
    }

    const columnsDesktop = Number(section.dataset.columnsDesktop || 4);
    const columnsMobile = Number(section.dataset.columnsMobile || 2);

    const enableDesktopSlider =
      section.dataset.enableDesktopSlider === 'true';

    const swipeOnMobile =
      section.dataset.swipeOnMobile === 'true';

    const needsDesktopSlider =
      enableDesktopSlider && productCount > columnsDesktop;

    const needsMobileSlider =
      swipeOnMobile && productCount > columnsMobile;

    sliderButtons.hidden = !(
      needsDesktopSlider ||
      needsMobileSlider
    );
  };

  /**
   * 最近チェックした商品セクションを初期化
   */
  const initRecentlyViewedSection = async (section) => {
    if (!section || section.dataset.recentlyViewedInitialized === 'true') {
      return;
    }

    section.dataset.recentlyViewedInitialized = 'true';

    const sectionId = section.dataset.sectionId;
    const currentHandle = section.dataset.currentProductHandle || '';
    const productsToShow = Number(section.dataset.productsToShow || 4);

    const productList = section.querySelector(
      '[data-recently-viewed-list]'
    );

    if (!sectionId || !productList) {
      return;
    }

    /*
     * 現在の商品を履歴へ保存。
     * 新しく見た商品ほど先頭に入る。
     */
    const recentlyViewedProducts = addCurrentProduct(currentHandle);

    /*
     * 現在表示している商品は
     * 「最近チェックした商品」から除外。
     */
    const productsToDisplay = recentlyViewedProducts
      .filter((handle) => handle !== currentHandle)
      .slice(0, productsToShow);

    if (!productsToDisplay.length) {
      section.hidden = true;
      return;
    }

    /*
     * 閲覧順を維持するためPromise.allで一括取得。
     */
    const cards = await Promise.all(
      productsToDisplay.map((handle) =>
        fetchProductCard(handle, sectionId)
      )
    );

    const validCards = cards.filter(Boolean);

    if (!validCards.length) {
      section.hidden = true;
      return;
    }

    validCards.forEach((card, index) => {
      card.id = `Slide-${sectionId}-${index + 1}`;

      if (
        window.Shopify &&
        window.Shopify.designMode
      ) {
        card.dataset.shopifyEditorBlock = '';
      }

      productList.appendChild(card);
    });

    updateSliderButtons(section, validCards.length);

    /*
     * 商品が1件以上取得できたらセクションを表示。
     */
    section.hidden = false;

    /*
     * DOM反映後にDawnのslider-componentを更新。
     */
    requestAnimationFrame(() => {
      refreshSlider(section);
    });
  };

  /**
   * ページ内すべての最近チェック商品セクションを初期化
   */
  const initAllRecentlyViewedSections = (root = document) => {
    root
      .querySelectorAll('[data-recently-viewed-section]')
      .forEach((section) => {
        initRecentlyViewedSection(section);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllRecentlyViewedSections();
    });
  } else {
    initAllRecentlyViewedSections();
  }

  /**
   * Shopifyテーマエディタで
   * セクションを追加・再読み込みした場合にも初期化。
   */
  document.addEventListener('shopify:section:load', (event) => {
    initAllRecentlyViewedSections(event.target);
  });
})();